import { Button } from '@framework/ui/atoms/Button';
import { Input } from '@framework/ui/atoms/Input';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Timestamp } from '@framework/Timestamp';
import { buildDateTimeArgs } from '@framework/i18n/buildDateTimeArgs';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onSubmit(name: string): void;
  onClose(): void;
};

const buildDefaultName = (t: TFunction): string => t('common.dateTimeFormat', buildDateTimeArgs(Timestamp.now()));

export const ViewModelCreateModal: React.FC<Props> = ({ isOpen, onSubmit, onClose }: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>(() => buildDefaultName(t));
  const [canSave, setCanSave] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(buildDefaultName(t));
  }, [isOpen, t]);

  useEffect(() => {
    setCanSave(name.trim() !== '');
  }, [name]);

  const handleSubmit = useCallback(() => {
    onSubmit(name);
    onClose();
  }, [name, onClose, onSubmit]);

  const handleClose = useCallback(() => {
    onClose();
    setName('');
  }, [onClose]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.key !== 'Enter' || !canSave) return;
    handleSubmit();
  };

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workspace.viewModelCreate.title')}
      submitButton={
        <Button onClick={handleSubmit} disabled={!canSave}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button onClick={handleClose} color={'secondary'}>
          {t('common.action.cancel')}
        </Button>
      }
      shouldReturnFocusAfterClose={false}
    >
      <div className="p-2">
        <Input
          type="text"
          placeholder={t('workspace.viewModelCreate.namePlaceholder')}
          value={name}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          autoFocus={true}
          autoSelect={true}
        />
      </div>
    </GeneralModal>
  );
};
